import classcat from "classcat";
import React from "react";
import Drawer from "react-modern-drawer";

import { Spinner } from "@touchtech/web-baselayer-ui";

import Footer from "@is/components/Footer";
import LanguageAndCountryButton from "@is/components/LanguageAndCountryButton";
import LanguageAndCurrencyModal from "@is/components/LanguageAndCurrencyModal";
import PrimaryAttributeHeader from "@is/components/PrimaryAttributeHeader";
import SearchButton from "@is/components/SearchButton";
import SignUpButton from "@is/components/SignUpButton";
import SignUpModal from "@is/components/SignUpModal";
import { PRIMARY_ATTRIBUTE_DRAWER_STYLES } from "@is/constants/preferences";
import { SearchTerm } from "@is/types";

import AttributeOption from "./AttributeOption";
import css from "./PrimaryAttributeDrawer.module.css";
import useManagePrimaryAttributeDrawer from "./hooks/useManagePrimaryAttributeDrawer";

type Props = {
  onNavigate: (searchTerms: SearchTerm[]) => void;
};

const PrimaryAttributeDrawer = ({ onNavigate }: Props) => {
  const {
    isOpen,
    initialSearchTermsAreSet,
    isPending,
    showPrimaryAttributeButtons,
    handleToggleDrawer,
    handleCloseDrawer,
    attributes,
    currentOptions,
    handlePrimaryAttributeClick,
    handleSelectOption,
    showLanguageAndCurrencyModal,
    handleToggleLanguageAndCurrencyModal,
    handleSettingDrawerOpen,
    handleToggleSignUpModal,
    showSignUpModal,
    showSignUpButton,
    signUpButtonTitle,
  } = useManagePrimaryAttributeDrawer(onNavigate);

  return (
    <>
      <div className={css.closedContainer}>
        <div
          className={classcat([
            css.actionButtonsContainer,
            { [css.show]: showPrimaryAttributeButtons },
          ])}
        >
          <div className={css.signUpButtonContainer}>
            {showSignUpButton && (
              <SignUpButton
                title={signUpButtonTitle}
                primary
                onClick={handleToggleSignUpModal}
              />
            )}
          </div>
          <LanguageAndCountryButton
            primary
            onClick={handleToggleLanguageAndCurrencyModal}
          />
        </div>
        <div>
          <div className={css.searchButtonContainer}>
            <SearchButton
              onClick={handleToggleDrawer}
              isVisible={
                showPrimaryAttributeButtons && initialSearchTermsAreSet
              }
            />
          </div>
        </div>
      </div>

      <Drawer
        open={isOpen}
        size="auto"
        direction="top"
        enableOverlay={true}
        overlayOpacity={0}
        style={PRIMARY_ATTRIBUTE_DRAWER_STYLES}
        onClose={handleCloseDrawer}
      >
        <div
          className={classcat([css.drawerContent, { [css.active]: isOpen }])}
        >
          <PrimaryAttributeHeader
            attributes={attributes}
            onAttributeClick={handlePrimaryAttributeClick}
            onLanguageAndCurrencyClick={handleToggleLanguageAndCurrencyModal}
            onSignUpClick={handleToggleSignUpModal}
            showSignUpButton={showSignUpButton}
            signUpButtonTitle={signUpButtonTitle}
          />

          <div className={css.searchContainer} />

          {!isPending ? (
            <div
              className={classcat([
                {
                  [css.optionsThreeColumns]: currentOptions.length > 2,
                  [css.optionsTwoColumns]: currentOptions.length <= 2,
                },
              ])}
            >
              {currentOptions.map((option) => (
                <AttributeOption
                  key={option.value}
                  value={option}
                  onClick={handleSelectOption}
                />
              ))}
            </div>
          ) : (
            <div className={css.spinnerContainer}>
              <Spinner className="text-headline-5xl" />
            </div>
          )}
        </div>
      </Drawer>
      <Footer
        isOpen={isOpen}
        styles={PRIMARY_ATTRIBUTE_DRAWER_STYLES}
        borderTop={false}
        onLogoClick={handleSettingDrawerOpen}
        onClose={handleCloseDrawer}
      />
      {showLanguageAndCurrencyModal && (
        <LanguageAndCurrencyModal
          isOpen={true}
          onClose={handleToggleLanguageAndCurrencyModal}
        />
      )}
      {showSignUpModal && (
        <SignUpModal isOpen onClose={handleToggleSignUpModal} />
      )}
    </>
  );
};

export default PrimaryAttributeDrawer;
